import { ViewConfig } from '@helo/cms'

import {
  DetailSubTableProbeRow,
  DetailSubTableTarget,
  DetailSubTablesTest,
} from '../DetailSubTablesTestPage/DetailSubTablesTestPage.parts'
import {
  ListTableProbeRow,
  ListTablesTest,
  MainListTableTarget,
} from '../ListTablesTestPage/ListTablesTestPage.parts'
import { DetailSubTableBatchProbe } from './detailSubTableBatchProbe.parts'
import { PROBE_CONCURRENCY, ProbeConcurrency } from './probeConcurrency.parts'
import { TableProbeQueryContext } from './tableProbeQueryContext.types'
import {
  LIST_RECORD_LINK_BY_QUERY_ID,
  PROBE_INITIAL_ROW_COUNT,
  ProbeRunResult,
  TableProbeRunner,
} from './tableProbeRunner.parts'

const groupTargetsByViewId = (
  targets: DetailSubTableTarget[],
): DetailSubTableTarget[][] => {
  const groups = new Map<string, DetailSubTableTarget[]>()

  targets.forEach(target => {
    const existing = groups.get(target.viewId) ?? []
    groups.set(target.viewId, [...existing, target])
  })

  return [...groups.values()]
}

const findParentListTarget = (
  viewConfigs: ViewConfig[],
  parentListQueryId: string,
): MainListTableTarget | undefined =>
  ListTablesTest.collectMainListTables(viewConfigs).find(
    target => target.queryId === parentListQueryId,
  )

const toProbeRowResult = (
  result: ProbeRunResult,
): Partial<DetailSubTableProbeRow> => ({
  ...result,
  parentEntityId: result.passEntityId,
})

const runListTableProbe = async (
  context: TableProbeQueryContext,
  target: MainListTableTarget,
  onProgress?: (recordsChecked: number, totalCount: number) => void,
): Promise<Partial<ListTableProbeRow>> => {
  const probeQueryId = ListTablesTest.createProbeQueryId(target.queryId)
  const entityIdDataKey =
    LIST_RECORD_LINK_BY_QUERY_ID[target.queryId]?.entityIdDataKey

  try {
    context.registerQuery({
      id: probeQueryId,
      queryDefinitionId: target.queryConfig.queryDefinitionId,
      paginated: true,
      variables: target.queryConfig.variables ?? {},
    })

    return await TableProbeRunner.runPaginatedListProbe({
      connectionLocator: target.connectionLocator,
      sampleColumnSelector: target.sampleColumnSelector,
      listViewPath: target.viewPath,
      queryId: target.queryId,
      entityIdDataKey,
      onProgress,
      fetchPage: async (skip, first) =>
        context.updateVariablesAndRefetch(probeQueryId, {
          ...(target.queryConfig.variables ?? {}),
          first,
          skip,
        }),
    })
  } catch (error) {
    return {
      status: 'error',
      errorMessage:
        error instanceof Error ? error.message : 'Unknown probe error',
    }
  } finally {
    context.removeQuery(probeQueryId)
  }
}

const runDetailViewBatchWithProgress = async (
  context: TableProbeQueryContext,
  viewTargets: DetailSubTableTarget[],
  parentListTarget: MainListTableTarget,
  onBatchProgress?: (
    queryIds: string[],
    parentsChecked: number,
    parentTotalCount: number,
  ) => void,
): Promise<Record<string, Partial<DetailSubTableProbeRow>>> => {
  const parentListSource = viewTargets[0].parentListSource
  const parentProbeQueryId = DetailSubTablesTest.createParentListProbeQueryId(
    parentListSource.parentListQueryId,
  )
  const probeQueryIds = viewTargets.map(target =>
    DetailSubTablesTest.createProbeQueryId(target.queryId),
  )

  try {
    context.registerQuery({
      id: parentProbeQueryId,
      queryDefinitionId: parentListTarget.queryConfig.queryDefinitionId,
      paginated: true,
      variables: parentListTarget.queryConfig.variables ?? {},
    })

    viewTargets.forEach(target => {
      context.registerQuery({
        id: DetailSubTablesTest.createProbeQueryId(target.queryId),
        queryDefinitionId: target.queryConfig.queryDefinitionId,
        paginated: true,
        variables: target.queryConfig.variables ?? {},
      })
    })

    const batchResults = await DetailSubTableBatchProbe.run({
      targets: viewTargets.map(target => ({
        queryId: target.queryId,
        connectionLocator: target.connectionLocator,
        sampleColumnSelector: target.sampleColumnSelector,
        detailViewPath: target.viewPath,
        fetchSubTablePage: async parentEntityId => {
          const probeQueryId = DetailSubTablesTest.createProbeQueryId(
            target.queryId,
          )

          return context.updateVariablesAndRefetch(probeQueryId, {
            ...(target.queryConfig.variables ?? {}),
            id: parentEntityId,
            first: PROBE_INITIAL_ROW_COUNT,
            skip: 0,
          })
        },
      })),
      parentConnectionLocator: parentListTarget.connectionLocator,
      parentEntityIdDataKey: parentListSource.entityIdDataKey,
      rawEntityIdPath: parentListSource.rawEntityIdPath,
      onProgress: ({ pendingQueryIds, parentsChecked, parentTotalCount }) => {
        onBatchProgress?.(pendingQueryIds, parentsChecked, parentTotalCount)
      },
      fetchParentPage: async (skip, first) =>
        context.updateVariablesAndRefetch(parentProbeQueryId, {
          ...(parentListTarget.queryConfig.variables ?? {}),
          first,
          skip,
        }),
    })

    return Object.fromEntries(
      Object.entries(batchResults).map(([queryId, result]) => [
        queryId,
        toProbeRowResult(result),
      ]),
    )
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown probe error'

    return Object.fromEntries(
      viewTargets.map(target => [
        target.queryId,
        {
          status: 'error' as const,
          errorMessage,
        },
      ]),
    )
  } finally {
    probeQueryIds.forEach(queryId => context.removeQuery(queryId))
    context.removeQuery(parentProbeQueryId)
  }
}

const runDetailViewBatch = async (
  context: TableProbeQueryContext,
  viewTargets: DetailSubTableTarget[],
  parentListTarget: MainListTableTarget,
): Promise<Record<string, Partial<DetailSubTableProbeRow>>> =>
  runDetailViewBatchWithProgress(context, viewTargets, parentListTarget)

const runListTableProbes = async (
  context: TableProbeQueryContext,
  viewConfigs: ViewConfig[],
  options?: {
    onRowUpdate?: (queryId: string, update: Partial<ListTableProbeRow>) => void
    onRowProgress?: (
      queryId: string,
      recordsChecked: number,
      totalCount: number,
    ) => void
  },
): Promise<ListTableProbeRow[]> => {
  const targets = ListTablesTest.collectMainListTables(viewConfigs)
  const rows = ListTablesTest.toInitialProbeRows(targets)

  await ProbeConcurrency.runWithConcurrency(
    targets,
    PROBE_CONCURRENCY,
    async target => {
      const probeResult = await runListTableProbe(
        context,
        target,
        (recordsChecked, totalCount) => {
          options?.onRowProgress?.(target.queryId, recordsChecked, totalCount)
        },
      )
      const rowIndex = rows.findIndex(row => row.queryId === target.queryId)

      if (rowIndex >= 0) {
        rows[rowIndex] = {
          ...rows[rowIndex],
          ...probeResult,
        }
        options?.onRowUpdate?.(target.queryId, probeResult)
      }
    },
  )

  return rows
}

const runDetailSubTableProbes = async (
  context: TableProbeQueryContext,
  viewConfigs: ViewConfig[],
  options?: {
    onRowUpdate?: (
      queryId: string,
      update: Partial<DetailSubTableProbeRow>,
    ) => void
    onBatchProgress?: (
      queryIds: string[],
      parentsChecked: number,
      parentTotalCount: number,
    ) => void
  },
): Promise<DetailSubTableProbeRow[]> => {
  const targets = DetailSubTablesTest.collectDetailSubTables(viewConfigs)
  const viewGroups = groupTargetsByViewId(targets)
  const rows = DetailSubTablesTest.toInitialProbeRows(targets)

  await ProbeConcurrency.runWithConcurrency(
    viewGroups,
    PROBE_CONCURRENCY,
    async viewTargets => {
      const parentListTarget = findParentListTarget(
        viewConfigs,
        viewTargets[0].parentListSource.parentListQueryId,
      )

      if (!parentListTarget) {
        const errorMessage = `Parent list ${viewTargets[0].parentListSource.parentListQueryId} not found`

        viewTargets.forEach(target => {
          const update = {
            status: 'error' as const,
            errorMessage,
          }
          const rowIndex = rows.findIndex(row => row.queryId === target.queryId)

          if (rowIndex >= 0) {
            rows[rowIndex] = {
              ...rows[rowIndex],
              ...update,
            }
            options?.onRowUpdate?.(target.queryId, update)
          }
        })

        return
      }

      const batchResults = await runDetailViewBatchWithProgress(
        context,
        viewTargets,
        parentListTarget,
        options?.onBatchProgress,
      )

      Object.entries(batchResults).forEach(([queryId, result]) => {
        const rowIndex = rows.findIndex(row => row.queryId === queryId)

        if (rowIndex >= 0) {
          rows[rowIndex] = {
            ...rows[rowIndex],
            ...result,
          }
          options?.onRowUpdate?.(queryId, result)
        }
      })

      viewTargets.forEach(target => {
        if (batchResults[target.queryId]) {
          return
        }

        const rowIndex = rows.findIndex(row => row.queryId === target.queryId)

        if (rowIndex >= 0 && rows[rowIndex].status === 'running') {
          const update = {
            status: 'fail' as const,
            errorMessage:
              'No parent list entity returned rows for this sub-table',
          }
          rows[rowIndex] = {
            ...rows[rowIndex],
            ...update,
          }
          options?.onRowUpdate?.(target.queryId, update)
        }
      })
    },
  )

  return rows
}

export const TableProbeOrchestrator = {
  runDetailSubTableProbes,
  runListTableProbes,
}
